/**
 * Client-side filtering for the `lookupFetchers` typeahead contract used by
 * wig-react-library's `WigMetadataForm` / `WigMetadataCrudPanel`.
 *
 * The library's `LookupSelect` sets `filterOption={false}` and treats the
 * fetcher as the entire search, so any fetcher backed by an endpoint with no
 * server-side text filter has to narrow the list itself. A fetcher that
 * ignores its `query` argument returns the full list on every keystroke — the
 * dropdown never narrows and the `+ Add "..."` row reads as the only match.
 *
 * Space handling: interior AND trailing spaces are significant, because that
 * is how a user disambiguates a numeric-prefixed name — `89 ` must narrow to
 * `89 ENERGY LLC` and drop `894 OPERATING`. Only LEADING whitespace is
 * discarded, so a stray space before the first character doesn't blank the
 * list. A query that is entirely whitespace counts as no query at all and
 * leaves the list intact (the state the dropdown opens in).
 */

/** Lowercase a raw typeahead query and drop only its leading whitespace. */
export const normalizeLookupQuery = (query) =>
  String(query ?? "").toLowerCase().replace(/^\s+/, "");

/**
 * Narrow `{id, label}` lookup options to those whose label contains `query`
 * (case-insensitive substring). Returns the list untouched for a blank or
 * whitespace-only query. Never mutates the input.
 */
export const filterLookupOptions = (options, query) => {
  if (!Array.isArray(options)) return [];
  const q = normalizeLookupQuery(query);
  if (!q) return options;
  return options.filter((o) =>
    String(o?.label ?? "")
      .toLowerCase()
      .includes(q)
  );
};
